/**
 * AddContactModal — Add new contact to the org chart (Wave 12+).
 *
 * Allows adding contacts with:
 * - First/last name (required)
 * - Title, email, phone
 * - Reports to (dropdown of existing contacts)
 * - Relationship status
 * - Influence level
 */

import React, { useState } from 'react';
import { X, UserPlus, Loader2 } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import useModalBehavior from '../../hooks/useModalBehavior';
import './AddContactModal.css';
import OverlayPortal from '../shared/OverlayPortal';

const RELATIONSHIP_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'champion', label: 'Champion' },
  { value: 'strong', label: 'Strong' },
  { value: 'developing', label: 'Developing' },
  { value: 'new', label: 'New' },
  { value: 'blocker', label: 'Blocker' },
  { value: 'dormant', label: 'Dormant' },
];

const INFLUENCE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'decision_maker', label: 'Decision Maker' },
  { value: 'influencer', label: 'Influencer' },
  { value: 'blocker', label: 'Blocker' },
  { value: 'unknown', label: 'Unknown' },
];

function AddContactModal({
  onClose,
  onSuccess,
  contacts,
  accountId,
  departmentId,
  departmentLabel,
  preselectedManager,
}) {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    title: '',
    email: '',
    phone: '',
    reports_to_id: preselectedManager?.id || '',
    relationship_status: '',
    influence_level: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Anything typed makes the form dirty — a stray click on the scrim must
  // ask before destroying it (UX-AUDIT X-3).
  const modal = useModalBehavior(onClose, {
    isDirty: () => !!(form.first_name || form.last_name || form.title || form.email || form.phone),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) {
      setError('First name is required');
      return;
    }
    // CRM-15: an account no longer needs to exist before the first contact —
    // the backend creates it lazily from the department when account_id is
    // absent. Only block if we have neither.
    if (!accountId && !departmentLabel) {
      setError('No account or agency context. Please reopen the workspace.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        account_id: accountId,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || null,
        title: form.title.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        reports_to_id: form.reports_to_id || null,
        relationship_status: form.relationship_status || null,
        influence_level: form.influence_level || null,
        department_id: departmentId,
        department_label: departmentLabel,
      };

      await apiClient.post('/api/workspace/contacts', payload);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create contact');
    } finally {
      setSaving(false);
    }
  };

  // Build manager options from existing contacts
  const managerOptions = contacts
    .map((c) => ({
      value: c.id,
      label: `${c.first_name} ${c.last_name || ''}`.trim() + (c.title ? ` — ${c.title}` : ''),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <OverlayPortal>
    <div className="acm-overlay" {...modal.overlayProps}>
      <div className="acm-modal" {...modal.dialogProps} aria-label="Add contact" onClick={(e) => e.stopPropagation()}>
        <div className="acm-header">
          <div className="acm-header-title">
            <UserPlus size={18} />
            <span>Add contact</span>
          </div>
          <button className="acm-close" onClick={modal.requestClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form className="acm-form" onSubmit={handleSubmit}>
          {modal.confirming && (
            <div className="acm-error" role="alert">
              Discard this contact?
              <button type="button" className="acm-btn-secondary" style={{ marginLeft: 8 }} onClick={onClose}>Discard</button>
              <button type="button" className="acm-btn-secondary" style={{ marginLeft: 4 }} onClick={() => modal.setConfirming(false)}>Keep editing</button>
            </div>
          )}
          {error && <div className="acm-error">{error}</div>}

          <div className="acm-row">
            <div className="acm-field">
              <label htmlFor="first_name">
                First Name <span className="acm-required">*</span>
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                placeholder="John"
                autoFocus
              />
            </div>
            <div className="acm-field">
              <label htmlFor="last_name">Last Name</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="acm-field">
            <label htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="VP of Engineering"
            />
          </div>

          <div className="acm-row">
            <div className="acm-field">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="john.doe@company.com"
              />
            </div>
            <div className="acm-field">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div className="acm-field">
            <label htmlFor="reports_to_id">Reports To</label>
            <select
              id="reports_to_id"
              name="reports_to_id"
              value={form.reports_to_id}
              onChange={handleChange}
            >
              <option value="">— Top Level (no manager) —</option>
              {managerOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="acm-row">
            <div className="acm-field">
              <label htmlFor="relationship_status">Relationship</label>
              <select
                id="relationship_status"
                name="relationship_status"
                value={form.relationship_status}
                onChange={handleChange}
              >
                {RELATIONSHIP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="acm-field">
              <label htmlFor="influence_level">Influence</label>
              <select
                id="influence_level"
                name="influence_level"
                value={form.influence_level}
                onChange={handleChange}
              >
                {INFLUENCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="acm-actions">
            <button type="button" className="acm-btn-cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="acm-btn-submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 size={14} className="acm-spinner" />
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <UserPlus size={14} />
                  <span>Add contact</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    </OverlayPortal>
  );
}

export default AddContactModal;
